import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * @type {number | null}
   */
  #deactivateTimeout = null;

  /**
   * @type {number | null}
   */
  #closingTimeout = null;

  /**
   * Keep in sync with the desktop expand background collapse duration
   * (--submenu-animation-speed). The expanded-menu color scheme is held on the
   * header for this long so colors don't swap while the background is still
   * collapsing.
   * @type {number}
   */
  #closeAnimationDuration = 360;

  /**
   * @type {number}
   */
  #lastClientX = 0;

  /**
   * @type {number}
   */
  #lastClientY = 0;

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', this.#handlePointerLeave);
    this.overflowMenu?.addEventListener('pointerenter', this.#clearDeactivateTimeout);
    this.addEventListener('pointerover', this.#handleMenuPointerOver);
    this.#bindSubmenuPointerListeners();
    this.#cacheMaxOverflowMenuHeight();

    const header = this.headerComponent;
    if (header) {
      header.addEventListener('pointerleave', this.#handleHeaderPointerLeave);
    }

    document.addEventListener('pointermove', this.#handleDocumentPointerMove);

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearDeactivateTimeout();
    this.#clearClosingTimeout();
    window.removeEventListener('resize', this.#resizeListener);
    this.#cleanupMutationObserver();
    document.removeEventListener('pointermove', this.#handleDocumentPointerMove);

    const header = this.headerComponent;
    if (header) {
      header.removeEventListener('pointerleave', this.#handleHeaderPointerLeave);
    }

    this.removeEventListener('pointerover', this.#handleMenuPointerOver);
    this.#unbindSubmenuPointerListeners();
    this.overflowMenu?.removeEventListener('pointerenter', this.#clearDeactivateTimeout);
  }

  #bindSubmenuPointerListeners() {
    for (const submenu of this.querySelectorAll('[ref="submenu[]"]')) {
      submenu.addEventListener('pointerleave', this.#handlePointerLeave);
    }
  }

  #unbindSubmenuPointerListeners() {
    for (const submenu of this.querySelectorAll('[ref="submenu[]"]')) {
      submenu.removeEventListener('pointerleave', this.#handlePointerLeave);
    }
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    this.#cacheMaxOverflowMenuHeight();
    setHeaderMenuStyle();
  }, 100);

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#clearDeactivateTimeout();
    this.#clearClosingTimeout();
    this.headerComponent?.removeAttribute('data-menu-closing');
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';
    const previouslyActiveItem = this.#state.activeItem;
    const hasExpandableSubmenu = itemHasExpandableSubmenu(item);

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;

    if (!hasExpandableSubmenu) {
      this.#collapseSubmenuExpansion();
      return;
    }

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      submenu.dataset.active = '';

      this.#cleanupMutationObserver();

      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (this.#updateSubmenuHeight(submenu)) {
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    if (submenu) {
      this.#updateSubmenuHeight(submenu);
    }

    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const { relatedTarget } = event;

    if (relatedTarget instanceof Node && this.#isMenuTarget(relatedTarget)) {
      const currentItem = findMenuItem(event.target);
      const relatedItem =
        relatedTarget instanceof Element ? findMenuItem(relatedTarget) : null;

      if (
        currentItem &&
        itemHasExpandableSubmenu(currentItem) &&
        relatedItem &&
        !itemHasExpandableSubmenu(relatedItem)
      ) {
        this.#collapseSubmenuExpansion();
        this.#state.activeItem = relatedItem;
      }

      return;
    }

    this.#scheduleDeactivate();
  }

  #handlePointerLeave = (event) => {
    const { relatedTarget } = event;

    if (relatedTarget instanceof Node && this.#isMenuTarget(relatedTarget)) return;

    this.#scheduleDeactivate();
  };

  #handleDocumentPointerMove = (event) => {
    this.#lastClientX = event.clientX;
    this.#lastClientY = event.clientY;
  };

  #handleMenuPointerOver = (event) => {
    if (!this.#state.activeItem) return;

    if (event.target instanceof Element && this.#isMenuTarget(event.target)) {
      this.#clearDeactivateTimeout();
    }
  };

  #handleHeaderPointerLeave = (event) => {
    const { relatedTarget } = event;

    if (relatedTarget instanceof Node && this.#isMenuTarget(relatedTarget)) return;

    this.#scheduleDeactivate();
  };

  /**
   * @param {Node} node
   * @returns {boolean}
   */
  #isMenuTarget(node) {
    if (!(node instanceof Element)) return false;

    if (this.contains(node)) return true;

    const overflowMenu = this.overflowMenu;
    if (overflowMenu?.contains(node)) return true;

    if (node.closest('.menu-list__submenu, [part="overflow"]')) return true;

    return Boolean(node.closest('[slot="overflow"]'));
  }

  #isPointerOverMenu() {
    const element = document.elementFromPoint(this.#lastClientX, this.#lastClientY);

    return element instanceof Element && this.#isMenuTarget(element);
  }

  #scheduleDeactivate() {
    this.#clearDeactivateTimeout();

    this.#deactivateTimeout = window.setTimeout(() => {
      this.#deactivateTimeout = null;

      if (!this.#state.activeItem) return;

      if (this.#isPointerOverMenu()) return;

      this.#deactivate();
    }, 200);
  }

  #clearDeactivateTimeout() {
    if (this.#deactivateTimeout !== null) {
      clearTimeout(this.#deactivateTimeout);
      this.#deactivateTimeout = null;
    }
  }

  #clearClosingTimeout() {
    if (this.#closingTimeout !== null) {
      clearTimeout(this.#closingTimeout);
      this.#closingTimeout = null;
    }
  }

  #clearSubmenuActiveStates() {
    for (const activeSubmenu of this.querySelectorAll('[ref="submenu[]"][data-active]')) {
      delete activeSubmenu.dataset.active;
    }

    if (this.overflowMenu?.dataset.active) {
      delete this.overflowMenu.dataset.active;
    }
  }

  #collapseSubmenuExpansion() {
    this.#clearClosingTimeout();

    const item = this.#state.activeItem;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';
    this.ariaExpanded = 'false';

    if (item?.getAttribute('aria-haspopup') === 'true') {
      item.ariaExpanded = 'false';
    }

    this.#clearSubmenuActiveStates();
  }

  /**
   * Deactivate the active item with a synced close animation.
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.#closingTimeout !== null) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';
    // Collapse the single expandable background to zero height in one motion
    // (handled in CSS via [data-menu-closing]) while the expanded-menu color
    // scheme stays applied to the header until the collapse finishes.
    this.headerComponent?.setAttribute('data-menu-closing', 'true');

    const submenu = findSubmenu(item);

    this.#closingTimeout = window.setTimeout(() => {
      this.#closingTimeout = null;

      this.#state.activeItem = null;
      this.ariaExpanded = 'false';
      item.ariaExpanded = 'false';

      this.#clearSubmenuActiveStates();

      if (submenu) {
        delete submenu.dataset.active;
      }

      this.headerComponent?.removeAttribute('data-menu-closing');
    }, this.#closeAnimationDuration);
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Measure the active submenu's content height.
   * @param {HTMLElement} submenu
   * @returns {number}
   */
  #measureSubmenuHeight(submenu) {
    const inner =
      submenu.querySelector('.menu-list__submenu-inner') ??
      submenu.querySelector('[part="overflow-list"]');

    if (inner instanceof HTMLElement) {
      return inner.offsetHeight;
    }

    return submenu.scrollHeight;
  }

  /**
   * Update --submenu-height to match the active submenu.
   * @param {HTMLElement} submenu
   * @returns {boolean} Whether a non-zero height was applied
   */
  #updateSubmenuHeight(submenu) {
    const height = this.#measureSubmenuHeight(submenu);

    if (height <= 0) return false;

    this.headerComponent?.style.setProperty('--submenu-height', `${height}px`);
    return true;
  }

  /**
   * Caches the maximum height of all submenus for consistent animations
   * Stores the value in a CSS custom property for use in transitions
   */
  #cacheMaxOverflowMenuHeight() {
    const submenus = this.querySelectorAll('[ref="submenu[]"]');
    const maxHeight = Math.max(
      0,
      ...Array.from(submenus)
        .filter((submenu) => submenu instanceof HTMLElement)
        .map((submenu) => submenu.offsetHeight)
    );
    this.headerComponent?.style.setProperty('--submenu-max-height', `${maxHeight}px`);
  }

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Whether a menu item can open a submenu panel.
 * @param {HTMLElement | null} item
 * @returns {boolean}
 */
function itemHasExpandableSubmenu(item) {
  if (!item) return false;

  if (item.getAttribute('aria-haspopup') === 'true') return true;

  return Boolean(item.closest('[slot="more"]'));
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const moreItem = element.closest('[slot="more"]');
  if (moreItem) {
    return findMenuItem(moreItem.parentElement?.querySelector('[slot="overflow"]'));
  }

  if (element.matches('[ref="menuitem"]')) {
    return /** @type {HTMLElement} */ (element);
  }

  const menuitem = element.closest('[ref="menuitem"]');
  return menuitem instanceof HTMLElement ? menuitem : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  if (!(element instanceof Element)) return null;

  const submenu = element.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
